import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import ListLayout from '../../Generic/ListLayout';
import DataTable from '../../Generic/DataTable';
import confirmDeleteDialog from '../../Generic/confirmDeleteDialog';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useDeleteItem from '../../../hooks/useDeleteItem';
import useFilterData from '../../../hooks/useFilterData';
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { createGeneralColumn, createActiveColumn, createActionColumn } from '../../Generic/dataGridColumns';
import AuthContext from "../../../context/AuthProvider";
import FormSelect from '../../Generic/FormSelect';
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { Button, Modal, Card, Form, Badge } from 'react-bootstrap';

const List = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);
  const { mainStore, criteriaValueStore } = useStore();
  const { t } = useTranslation();
  const localizedTextsMap = useLocalizedTextsMap();
  useSetMenu(mainStore.CriteriaValueMenuType, mainStore.CriteriaValueMenuNo);

  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Batch mode state
  const [showBatchMode, setShowBatchMode] = useState(false);
  const [batchValues, setBatchValues] = useState("");
  const [parsedValues, setParsedValues] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);

  // Filter state for cascading selects:
  const [panels, setPanels] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [criteriaOptions, setCriteriaOptions] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState(null);

  const { register, setValue, formState: { errors } } = useForm({});

  // Process the batch input into an array of values
  useEffect(() => {
    if (batchValues.trim()) {
      // Split by newlines, commas, or semicolons
      const values = batchValues
        .split(/[\n,;]/)
        .map(val => val.trim())
        .filter(val => val.length > 0 && val.length <= 250);
      
      setParsedValues(values);
    } else {
      setParsedValues([]);
    }
  }, [batchValues]);

  // Function to handle the batch creation
  const handleBatchSubmit = async () => {
    if (!selectedCriteria) {
      enqueueSnackbar(t("pleaseSelectCriteria"), { variant: "warning" });
      return;
    }
    
    if (parsedValues.length === 0) {
      enqueueSnackbar(t("pleaseAddAtLeastOneValue"), { variant: "warning" });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const batchRequest = {
        criteriaId: parseInt(selectedCriteria.value, 10),
        panelId: parseInt(selectedPanel.value, 10),
        categoryId: parseInt(selectedCategory.value, 10),
        subcategoryId: parseInt(selectedSubcategory.value, 10),
        values: parsedValues
      };
      
      await axiosPrivate.post("/CriteriaValues/BatchCreate", batchRequest);
      enqueueSnackbar(t("batchCreateSuccess"), { variant: "success" });
      
      // Close the modal and refresh the data
      setShowBatchModal(false);
      setBatchValues("");
      
      // Refresh the list by re-fetching the data
      const queryParams = [];
      if (selectedPanel) queryParams.push(`panelId=${selectedPanel.value}`);
      if (selectedCategory) queryParams.push(`categoryId=${selectedCategory.value}`);
      if (selectedSubcategory) queryParams.push(`subcategoryId=${selectedSubcategory.value}`);
      if (selectedCriteria) queryParams.push(`criteriaId=${selectedCriteria.value}`);
      const queryString = queryParams.length ? `?${queryParams.join('&')}` : "";
      const response = await axiosPrivate.get(`/CriteriaValues/Filtered${queryString}`);
      if (Array.isArray(response.data)) {
        criteriaValueStore.setData(response.data);
      } else {
        criteriaValueStore.setData([]);
      }
      
    } catch (error) {
      enqueueSnackbar(t("batchCreateError"), { variant: "error" });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load panels on mount
  useEffect(() => {
    const loadPanels = async () => {
      try {
        const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
        setPanels(panelsResponse.data);
      } catch (err) {
        enqueueSnackbar(t("loadDataError"), { variant: "error" });
      }
    };
    loadPanels();
  }, [axiosPrivate, enqueueSnackbar, t]);

  // Handler for Panel change
  const handlePanelChange = async (selectedOption) => {
    if (!selectedOption) {
      // Clear panel and dependent selects
      setSelectedPanel(null);
      setCategories([]);
      setSubcategories([]);
      setCriteriaOptions([]);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSelectedCriteria(null);
      return;
    }
    setSelectedPanel(selectedOption);
    try {
      const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
      setCategories(
        typeof response.data === "string" && response.data === "No records" ? [] : response.data
      );
      // Clear dependent selects
      setSubcategories([]);
      setCriteriaOptions([]);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSelectedCriteria(null);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
    }
  };

  // Handler for Category change
  const handleCategoryChange = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedCategory(null);
      setSubcategories([]);
      setCriteriaOptions([]);
      setSelectedSubcategory(null);
      setSelectedCriteria(null);
      return;
    }
    setSelectedCategory(selectedOption);
    try {
      const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
      setSubcategories(
        typeof response.data === "string" && response.data === "No records" ? [] : response.data
      );
      // Clear dependent selects
      setCriteriaOptions([]);
      setSelectedSubcategory(null);
      setSelectedCriteria(null);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
    }
  };

  // Handler for Subcategory change
  const handleSubcategoryChange = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedSubcategory(null);
      setCriteriaOptions([]);
      setSelectedCriteria(null);
      return;
    }
    setSelectedSubcategory(selectedOption);
    try {
      // Load criteria options for the selected subcategory.
      const response = await axiosPrivate.get(`/Criterias/SelectOptionsBySubcategory/${selectedOption.value}`);
      setCriteriaOptions(
        typeof response.data === "string" && response.data === "No records" ? [] : response.data
      );
      // Clear previously selected criteria.
      setSelectedCriteria(null);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCriteria"), { variant: "error" });
    }
  };

  // Handler for Criteria change (the last filter)
  const handleCriteriaChange = (selectedOption) => {
    if (!selectedOption) {
      setSelectedCriteria(null);
      return;
    }
    setSelectedCriteria(selectedOption);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Fetch filtered CriteriaValues whenever any filter changes (or on initial load)
  useEffect(() => {
    const fetchCriteriaValues = async () => {
      try {
        const queryParams = [];
        if (selectedPanel) queryParams.push(`panelId=${selectedPanel.value}`);
        if (selectedCategory) queryParams.push(`categoryId=${selectedCategory.value}`);
        if (selectedSubcategory) queryParams.push(`subcategoryId=${selectedSubcategory.value}`);
        if (selectedCriteria) queryParams.push(`criteriaId=${selectedCriteria.value}`);
        const queryString = queryParams.length ? `?${queryParams.join('&')}` : "";
        const response = await axiosPrivate.get(`/CriteriaValues/Filtered${queryString}`);
        if (Array.isArray(response.data)) {
          criteriaValueStore.setData(response.data);
        } else {
          criteriaValueStore.setData([]);
        }
      } catch (error) {
        enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
      }
    };
    fetchCriteriaValues();
  }, [
    selectedPanel,
    selectedCategory,
    selectedSubcategory,
    selectedCriteria,
    axiosPrivate,
    criteriaValueStore,
    enqueueSnackbar,
    t,
  ]);

  // Delete operation remains unchanged
  const deleteCriteriaValue = useDeleteItem(axiosPrivate, '/CriteriaValues', criteriaValueStore.deleteCriteriaValue);
  const remove = useCallback((id) => {
    confirmDeleteDialog(t, deleteCriteriaValue, id);
  }, [deleteCriteriaValue, t]);

  // Create columns using useMemo
  const columns = useMemo(() => [
    createGeneralColumn("value", "criteriaValue", t),
    createGeneralColumn("criteriaName", "criteria", t),
    createGeneralColumn("subcategoryName", "subcategory", t),
    createGeneralColumn("categoryName", "category", t),
    createGeneralColumn("panelName", "panel", t),
    createActiveColumn(t),
    createActionColumn(t, "/CriteriaValue/Edit", remove)
  ], [t, remove]);

  const filteredData = useFilterData(toJS(criteriaValueStore.criteriaValue), searchQuery);
  
  // Function to open the batch modal
  const openBatchModal = () => {
    if (!selectedCriteria) {
      enqueueSnackbar(t("pleaseSelectCriteriaForBatch"), { variant: "warning" });
      return;
    }
    setShowBatchModal(true);
  };

  return (
    <>
      <ListLayout
        title={t("criteriaValueListTitle")}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t("searchPlaceholder")}
        addButtonLink="/CriteriaValue/Add"
        addButtonTitle={t("add")}
      >
        <div className="row mb-3">
          <FormSelect
            label="panel"
            name="panelId"
            register={register}
            errors={errors}
            options={panels}
            onChange={handlePanelChange}
            isClearable={true}
            colSize={3}
          />
          <FormSelect
            label="category"
            name="categoryId"
            value={selectedCategory}
            register={register}
            errors={errors}
            options={categories}
            onChange={handleCategoryChange}
            isClearable={true}
            colSize={3}
          />
          <FormSelect
            label="subcategory"
            name="subcategoryId"
            value={selectedSubcategory}
            register={register}
            errors={errors}
            options={subcategories}
            onChange={handleSubcategoryChange}
            isClearable={true}
            colSize={3}
          />
          <FormSelect
            label="criteria"
            name="criteriaId"
            value={selectedCriteria}
            register={register}
            errors={errors}
            options={criteriaOptions}
            onChange={handleCriteriaChange}
            isClearable={true}
            colSize={3}
          />
        </div>
        
        {/* Add batch mode button */}
        {selectedCriteria && (
          <div className="row mb-3">
            <div className="col-12">
              <Button 
                variant="outline-secondary" 
                className="ms-2"
                onClick={openBatchModal}
              >
                <i className="fas fa-layer-group me-1"></i> {t("addMultipleValues")}
              </Button>
            </div>
          </div>
        )}
        
        <DataTable
          rows={filteredData}
          columns={columns}
          pageSize={pageSize}
          setPageSize={setPageSize}
          localeTextsMap={localizedTextsMap}
        />
        
        {filteredData.length === 0 && (
          <div className="text-center mt-3 p-3 bg-light">
            <p>{t("noCriteriaValuesFound")}</p>
          </div>
        )}
      </ListLayout>
      
      {/* Batch creation modal */}
      <Modal show={showBatchModal} onHide={() => setShowBatchModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("addMultipleValuesForCriteria")}: {selectedCriteria?.label}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("enterMultipleValues")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={batchValues}
                onChange={(e) => setBatchValues(e.target.value)}
                placeholder={t("enterValuesSeperatedBy")}
              />
              <Form.Text className="text-muted">
                {t("batchValuesHelp")}
              </Form.Text>
            </Form.Group>
            
            {parsedValues.length > 0 && (
              <div className="mt-3">
                <h6>{t("valuePreview")} ({parsedValues.length})</h6>
                <div className="border p-2 bg-light">
                  {parsedValues.map((val, index) => (
                    <Badge bg="secondary" className="me-1 mb-1" key={index}>{val}</Badge>
                  ))}
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBatchModal(false)}>
            {t("cancel")}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBatchSubmit}
            disabled={isSubmitting || parsedValues.length === 0}
          >
            {isSubmitting ? t("submitting") : t("addValues")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default observer(List);

// import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
// import { toJS } from 'mobx';
// import { observer } from 'mobx-react-lite';
// import { useTranslation } from 'react-i18next';
// import { useStore } from '../../../stores/store';
// import ListLayout from '../../Generic/ListLayout';
// import DataTable from '../../Generic/DataTable';
// import confirmDeleteDialog from '../../Generic/confirmDeleteDialog';
// import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
// import useDeleteItem from '../../../hooks/useDeleteItem';
// import useFilterData from '../../../hooks/useFilterData';
// import useFetchData from '../../../hooks/useFetchData';
// import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
// import { useSetMenu } from "../../../hooks/useSetMenu";
// import { createGeneralColumn, createActiveColumn, createActionColumn } from '../../Generic/dataGridColumns';
// import AuthContext from "../../../context/AuthProvider";
// const List = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const { auth } = useContext(AuthContext);
//   const { mainStore, criteriaValueStore } = useStore();
//   const { t } = useTranslation();
//   const localizedTextsMap = useLocalizedTextsMap();
//   useSetMenu(mainStore.CriteriaValueMenuType, mainStore.CriteriaValueMenuNo);
//   const [columns, setColumns] = useState([]);
//   const [pageSize, setPageSize] = useState(10);
//   const [searchQuery, setSearchQuery] = useState("");

//   //FetchData and Search


//   useFetchData(axiosPrivate, `/CriteriaValues`, criteriaValueStore);
//   const filteredData = useFilterData(toJS(criteriaValueStore.criteriaValue), searchQuery);

//   const handleSearchChange = (event) => {
//     setSearchQuery(event.target.value);
//   };

//   //Delete
//   const deleteCriteriaValue = useDeleteItem(axiosPrivate, '/CriteriaValues', criteriaValueStore.deleteCriteriaValue);

//   const remove = useCallback((id) => {
//     confirmDeleteDialog(t, deleteCriteriaValue, id);
//   }, [deleteCriteriaValue, t]);

//   const cols = useMemo(() => [
//     createGeneralColumn("criteriaName", "criteria", t),
//     createGeneralColumn("value", "criteriaValue", t),
//     createGeneralColumn("subcategoryName", "subcategory", t),
//     createGeneralColumn("categoryName", "category", t),
//     createGeneralColumn("panelName", "panel", t),
//     createActiveColumn(t),
//     createActionColumn(t, "/CriteriaValue/Edit", remove),
//     // eslint-disable-next-line
//   ].filter(Boolean), [t, remove]);

//   useEffect(() => {
//     setColumns(cols);
//     // eslint-disable-next-line
//   }, [t]);

//   return (
//     <>
//       <ListLayout
//         title={t("criteriaValueListTitle")}
//         searchQuery={searchQuery}
//         onSearchChange={handleSearchChange}
//         searchPlaceholder={t("searchPlaceholder")}
//         addButtonLink="/CriteriaValue/Add"
//         addButtonTitle={t("add")}
//       >
//         <DataTable
//           rows={filteredData}
//           columns={columns}
//           pageSize={pageSize}
//           setPageSize={setPageSize}
//           localeTextsMap={localizedTextsMap}
//         />
//       </ListLayout>
//     </>
//   );
// };

// export default observer(List);